import { tryCatch } from 'ramda'
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { useLocalStorage } from '../../../hooks/useLocalStorage'
import { prescriptionBuilderReducer } from '../builder/reducer'
import { Prescription, PrescriptionAction, PrescriptionRuntype } from '../builder/types'

export type UsePrescriptionBuilderDispatch = (action: PrescriptionAction) => void

export type UsePrescriptionBuilder = () => {
  prescription: Prescription
  dispatch: UsePrescriptionBuilderDispatch
  loading: boolean
}

const initialPrescription: Prescription = {
  products: [],
  promoCode: '',
}

const useLivePrescriptionBuilder = () => {
  const [loading, setLoading] = useState(true)
  const [prescriptionStringified, setPrescriptionStringified, clearPrescription] = useLocalStorage(
    'prescription',
    JSON.stringify([]),
  )

  const setPrescription = useCallback(
    (p: Prescription) => setPrescriptionStringified(JSON.stringify(p)),
    [setPrescriptionStringified],
  )

  const prescription = useMemo(
    () =>
      tryCatch(
        () => {
          const parsedPrescription = JSON.parse(prescriptionStringified)
          if (PrescriptionRuntype.guard(parsedPrescription)) {
            return parsedPrescription
          }
          throw new Error('eScript is not valid')
        },
        () => {
          clearPrescription()
          return initialPrescription
        },
      )(),
    [clearPrescription, prescriptionStringified],
  )

  useEffect(() => {
    setLoading(false) // once parsing is done, we can stop showing the loading indicator
  }, [prescription, prescriptionStringified])

  const dispatch = useCallback(
    (action: PrescriptionAction) =>
      setPrescription(prescriptionBuilderReducer(prescription, action)),
    [prescription, setPrescription],
  )

  return useMemo(() => ({ prescription, dispatch, loading }), [prescription, dispatch, loading])
}

export const PrescriptionBuilderContext = createContext<
  ReturnType<UsePrescriptionBuilder> | undefined
>(undefined)

export const usePrescriptionBuilder: UsePrescriptionBuilder = () => {
  const livePrescriptionBuilder = useLivePrescriptionBuilder()
  const context = useContext(PrescriptionBuilderContext)

  // default to live prescription builder if no context (to avoid breaking changes)
  return context ? context : livePrescriptionBuilder
}
